import { useState } from 'react';
import { Card, Divider, Collapse, IconButton } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import Page from 'src/components/page/Page';
import MDButton from 'src/components/md/MDButton';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import Icon from '@mui/material/Icon';
import FamousSalesOrdersTable from './components/FamousSalesOrdersTable';
import ImportFamousOrderModal from './components/ImportFamousOrderModal';
// import CreateSalesOrderModal from './components/create-sales-order-modal/CreateSalesOrderModal';

const NoteToUser = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Card
      sx={{
        p: 4,
        mb: 3,
        background: 'linear-gradient(to right, #ffffff, #f8f9fa)',
        boxShadow: '0 2px 12px 0 rgba(0,0,0,0.05)',
        borderRadius: 2,
      }}
    >
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{ cursor: 'pointer' }}
      >
        <MDBox display="flex" alignItems="center">
          <MDTypography variant="h5" color="info" fontWeight="medium">
            ⚠️ READ THESE INSTRUCTIONS BEFORE USING{' '}
            {isExpanded ? null : '(click to expand)'}
          </MDTypography>
        </MDBox>
        <IconButton
          size="small"
          sx={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
          }}
        >
          <Icon fontSize="small" color="info">
            expand_more
          </Icon>
        </IconButton>
      </MDBox>

      <Collapse in={isExpanded} timeout={300}>
        <MDBox mt={3}>
          <MDBox mb={4} pl={0.5}>
            <MDTypography variant="body2" color="text" sx={{ opacity: 0.8 }}>
              This is a temporary page to enable EDI transactions for orders
              managed in Famous. Please follow these steps carefully:
            </MDTypography>
          </MDBox>

          <MDBox
            component="ol"
            sx={{
              pl: 4,
              '& > li': {
                mb: 2,
                color: 'text.primary',
                '&:last-child': {
                  mb: 0,
                },
              },
            }}
          >
            <MDBox component="li">
              <MDTypography
                variant="body2"
                fontWeight="medium"
                component="span"
              >
                Invoice the order in Famous
              </MDTypography>
              <MDTypography
                variant="body2"
                component="span"
                sx={{ opacity: 0.8 }}
              >
                {` - Complete the invoicing process in Famous`}
              </MDTypography>
            </MDBox>

            <MDBox component="li">
              <MDTypography
                variant="body2"
                fontWeight="medium"
                component="span"
              >
                Import the order
              </MDTypography>
              <MDTypography
                variant="body2"
                component="span"
                sx={{ opacity: 0.8 }}
              >
                {` - Click the "Import Famous Order" button above`}
              </MDTypography>
            </MDBox>

            <MDBox component="li">
              <MDTypography
                variant="body2"
                fontWeight="medium"
                component="span"
              >
                Review the information
              </MDTypography>
              <MDTypography
                variant="body2"
                component="span"
                sx={{ opacity: 0.8 }}
              >
                {` - Once loaded, carefully vet all order details`}
              </MDTypography>
            </MDBox>

            <MDBox component="li">
              <MDTypography
                variant="body2"
                fontWeight="medium"
                component="span"
              >
                Send EDI transaction
              </MDTypography>
              <MDTypography
                variant="body2"
                component="span"
                sx={{ opacity: 0.8 }}
              >
                {` - If everything looks correct, click "Send 810"`}
              </MDTypography>
            </MDBox>

            <MDBox component="li">
              <MDTypography
                variant="body2"
                fontWeight="medium"
                component="span"
              >
                Contact support if needed
              </MDTypography>
              <MDTypography
                variant="body2"
                component="span"
                sx={{ opacity: 0.8 }}
              >
                {` - If anything fails, reach out immediately to the Gently team`}
              </MDTypography>
            </MDBox>
          </MDBox>

          <Divider sx={{ my: 3 }} />

          <MDBox
            sx={{
              backgroundColor: (theme: Theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 152, 0, 0.1)'
                  : 'rgba(255, 152, 0, 0.05)',
              border: '1px solid',
              borderColor: 'warning.light',
              borderRadius: 2,
              p: 2.5,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Icon fontSize="small" color="warning">
              phone
            </Icon>
            <MDTypography variant="body2" color="warning.dark">
              <MDTypography
                component="span"
                variant="body2"
                fontWeight="medium"
                color="warning.dark"
              >
                Support Contact:
              </MDTypography>{' '}
              (857) 310-7458 - Call or text for immediate assistance
            </MDTypography>
          </MDBox>
        </MDBox>
      </Collapse>
    </Card>
  );
};

const FamousEDIPage: React.FC = () => {
  const [
    isImportFamousSalesOrderModalOpen,
    setIsImportFamousSalesOrderModalOpen,
  ] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);

  const handleImportSuccess = () => {
    setRefreshKey((prev) => prev + 1);
  };

  return (
    <Page
      title="Famous EDI"
      actions={
        <MDButton
          variant="text"
          color="info"
          onClick={() => setIsImportFamousSalesOrderModalOpen(true)}
        >
          Import Famous Order
        </MDButton>
      }
    >
      <NoteToUser />

      <Card sx={{ p: 2 }}>
        <FamousSalesOrdersTable key={refreshKey} />
      </Card>

      <ImportFamousOrderModal
        open={isImportFamousSalesOrderModalOpen}
        onClose={() => setIsImportFamousSalesOrderModalOpen(false)}
        onSuccess={handleImportSuccess}
      />
    </Page>
  );
};

export default FamousEDIPage;
